// 11 §11.1 PlaybackStore — builds the §9.1 plan and exposes it to the scene's EffectPlayer,
// which walks the stages with Phaser tweens and reports progress back here.
import { create } from 'zustand';
import type { ResolutionLog, Snapshot } from '../sim/types';
import { buildPlan, type AnimationPlan, type Speed } from '../playback/plan';
import { useSettings } from './settings';

export interface PlaybackStore {
  plan?: AnimationPlan; speed: Speed; playing: boolean; stageIndex: number;
  /** docs/56: the board as it stands part-way through the playback (the HUD shows this while it plays). */
  live?: Snapshot;
  setLive(live: Snapshot | undefined): void;
  /** docs/40 IU9: the stashes this playback has opened so far (their pickup cards need not wait for the end). */
  opened: readonly number[];
  markOpened(fixture: number): void;
  /** The pulse being played (docs/34: its server cues are looked up by encounter and pulse). */
  current?: { encounterId: number; pulseIndex: number };
  /** `encounterId`: the `encounter` row's id (the log's own is the sim's seed id). */
  /** `board`: the board as the pulse began (docs/47: where the spells fly from and to). */
  play(log: ResolutionLog, encounterId?: number, board?: Snapshot): void; skip(): void; setSpeed(s: Speed): void;
  // shell-internal: the EffectPlayer reports progress and completion
  advance(stageIndex: number): void; finish(): void;
  skipRequested: boolean;
  /** Set by the Encounter scene while it can animate; without it the controller swaps boards at once. */
  hasPlayer: boolean;
  setHasPlayer(v: boolean): void;
}

export const usePlayback = create<PlaybackStore>()((set, get) => ({
  speed: 1, playing: false, stageIndex: 0, skipRequested: false, hasPlayer: false, opened: [],
  setHasPlayer(hasPlayer) { set({ hasPlayer }); if (!hasPlayer && get().playing) get().finish(); },
  play(log, encounterId, board) {
    const settings = useSettings.getState();
    const plan = buildPlan(log, { reduceMotion: settings.reduceMotion, reduceIntensity: settings.effectIntensityBp < 5000, ...(settings.playbackSpeed !== 1 ? { speed: settings.playbackSpeed } : {}), ...(board ? { board } : {}) });
    set({ plan, speed: plan.speed, playing: true, stageIndex: 0, skipRequested: false, opened: [], current: { encounterId: encounterId ?? log.encounterId, pulseIndex: log.pulseIndex } });
  },
  skip() { set({ skipRequested: true }); },
  setSpeed(speed) { set({ speed }); useSettings.getState().set({ playbackSpeed: speed }); },
  advance(stageIndex) { set({ stageIndex }); },
  setLive(live) { set({ live }); },
  markOpened(fixture) { if (!get().opened.includes(fixture)) set({ opened: [...get().opened, fixture] }); },
  finish() { set({ playing: false, skipRequested: false, live: undefined, opened: [], stageIndex: get().plan?.stages.length ?? 0 }); },
}));
